<template>
  <div class="package-editor-container app-page app-page--fill">
    <el-card>
      <div class="main-content" ref="mainContentRef">
        <!-- 左侧检测项目包列表 -->
        <div class="left-panel" ref="leftPanelRef" :style="{ width: leftPanelWidth + 'px' }">
          <div class="list-header">
            <el-button v-if="can('新增')" type="primary" @click="addNewPackage">
              <el-icon>
                <Plus />
              </el-icon>
              新增
            </el-button>
            <el-button
              type="primary"
              circle
              size="small"
              class="app-refresh-btn"
              @click="loadPackageList"
              :loading="listLoading"
              :icon="Refresh"
              style="margin-left: auto;"
              title="刷新"
            />
          </div>

          <div class="package-list-wrapper">
            <DynamicTable
              :data="packageListData"
              :model="packageTableModel"
              :loading="listLoading"
              :show-column-selector="false"
              :show-export="false"
              :show-edit-button="can('查看')"
              :show-delete-button="can('删除')"
              :pagination="packagePagination"
              :active-row-key="activePackageRowKey"
              @edit="handlePackageEdit"
              @delete="handlePackageDelete"
              @filter="handleFilter"
              @page-change="handlePackagePageChange"
              @page-size-change="handlePackagePageSizeChange"
            />
          </div>
        </div>

        <!-- 拖拽分隔条 -->
        <div class="resize-handle" @mousedown="startResize"></div>

        <!-- 右侧包详情：名称输入 + 检测项目列表 -->
        <div class="right-panel" v-loading="detailLoading" element-loading-text="加载中...">
          <div v-if="currentPackage" class="edit-area">
            <!-- 编辑模式控制 -->
            <div class="edit-mode-control">
              <el-button v-if="!isEditMode && can('编辑')" type="primary" @click="enterEditMode">
                编辑
              </el-button>
              <el-button v-if="isEditMode" @click="exitEditMode">
                取消编辑
              </el-button>
            </div>

            <el-form
              ref="packageFormRef"
              :model="currentPackage"
              :rules="packageFormRules"
              label-width="150px"
              class="form-content"
            >
              <div class="package-name-row">
                <el-form-item class="package-name-item" label="原始记录包名称" prop="name">
                  <el-input 
                    v-model="currentPackage.name" 
                    placeholder="请输入原始记录包名称" 
                    :readonly="!isEditMode"
                  />
                </el-form-item>
                <div v-if="isEditMode" class="action-buttons">
                  <el-button v-if="can('保存')" type="primary" @click="savePackageData" :loading="saveLoading">保存数据</el-button>
                  <el-button @click="resetCurrentPackage" :disabled="saveLoading">重置</el-button>
                </div>
              </div>
              <div class="project-table-container">
                <!-- 查看模式：使用 el-table 纯查看 -->
                <el-table
                  v-if="!isEditMode"
                  :data="displayProjectListData"
                  :loading="projectListLoading"
                  stripe
                  border
                  style="width: 100%"
                  height="100%"
                >
                  <el-table-column
                    prop="名称"
                    label="名称"
                    min-width="150"
                  />
                  <el-table-column
                    prop="数量"
                    label="数量"
                    width="100"
                  />
                  <el-table-column
                    prop="验证数量"
                    label="验证数量"
                    width="120"
                  >
                    <template #default="{ row }">
                      {{ row.验证数量 ? '是' : '否' }}
                    </template>
                  </el-table-column>
                  <el-table-column
                    prop="转跳方向"
                    label="转跳方向"
                    width="120"
                  >
                    <template #default="{ row }">
                      {{ row.转跳方向 ? '是' : '否' }}
                    </template>
                  </el-table-column>
                  <el-table-column
                    prop="Id"
                    label="ID"
                    width="150"
                  />
                </el-table>
                
                <!-- 编辑模式：使用 DynamicTable 支持完整功能 -->
                <DynamicTable
                  v-else
                  ref="projectTableRef"
                  :data="displayProjectListData"
                  :model="projectTableModel"
                  :loading="projectListLoading"
                  :show-column-selector="true"
                  :show-export="false"
                  :show-edit-button="false"
                  :show-delete-button="false"
                  :showSelection="true"
                  :defaultSelection="selectedProjectsData"
                  :pagination="projectPagination"
                  @filter="handleProjectFilter"
                  @sort="handleProjectSort"
                  @selection-change="handleProjectSelectionChange"
                  @page-change="handleProjectPageChange"
                  @page-size-change="handleProjectPageSizeChange"
                />
              </div>
            </el-form>
          </div>
          <div v-else class="empty-state">
            <el-empty description="请先创建或选择一个检测项目包" />
          </div>
        </div>
      </div>
    </el-card>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted, nextTick, onUnmounted } from 'vue';
import { Plus, Refresh } from '@element-plus/icons-vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import DynamicTable from '@/components/DynamicTable.vue';
import { ParameterSettings } from '@/network/index';
import { useButtonPermission } from '@/utils/buttonPermission';

const { can } = useButtonPermission('/ParameterSettings/TestItemPackage/index.vue');

interface PackageFormData {
  id?: number | string;
  name: string;
}

// 左侧面板拖拽调整宽度相关
const mainContentRef = ref<HTMLElement | null>(null);
const leftPanelRef = ref<HTMLElement | null>(null);
const leftPanelWidth = ref<number>(300);
const isResizing = ref<boolean>(false);
const minPanelWidth = 200;

const startResize = (e: MouseEvent) => {
  isResizing.value = true;
  document.addEventListener('mousemove', handleResize);
  document.addEventListener('mouseup', stopResize);
  document.body.style.cursor = 'col-resize';
  document.body.style.userSelect = 'none';
};

const handleResize = (e: MouseEvent) => {
  if (!isResizing.value || !mainContentRef.value) return;
  const containerRect = mainContentRef.value.getBoundingClientRect();
  const maxWidth = containerRect.width / 2;
  let newWidth = e.clientX - containerRect.left;
  newWidth = Math.max(minPanelWidth, Math.min(maxWidth, newWidth));
  leftPanelWidth.value = newWidth;
};

const stopResize = () => {
  isResizing.value = false;
  document.removeEventListener('mousemove', handleResize);
  document.removeEventListener('mouseup', stopResize);
  document.body.style.cursor = '';
  document.body.style.userSelect = '';
};

onUnmounted(() => {
  document.removeEventListener('mousemove', handleResize);
  document.removeEventListener('mouseup', stopResize);
});

// 左侧检测项目包列表状态
const activePackageIndex = ref<number>(-1);
const packageList = ref<any[]>([]);
const packageTableModel = ref<Record<string, string> | undefined>(undefined);

const listLoading = ref<boolean>(false);
const detailLoading = ref<boolean>(false);
const saveLoading = ref<boolean>(false);

const filterParams = ref<any[]>([]);

// 分页相关状态（左侧列表使用紧凑布局）
const packagePagination = ref({
  show: true,
  page: 1,
  pageSize: 10,
  total: 0,
  pageSizes: [10, 20, 50, 100],
  layout: 'total, prev, pager, next', // 移除 sizes 和 jumper，使布局更紧凑
  small: true
});

// 当前编辑/查看的检测项目包
const currentPackage = ref<PackageFormData | null>(null);
const packageFormRef = ref<any>(null);

// 编辑模式状态
const isEditMode = ref<boolean>(false);

// DynamicTable 引用
const projectTableRef = ref<any>(null);

const packageFormRules = {
  name: [
    { required: true, message: '请输入原始记录包名称', trigger: 'blur' }
  ]
};

const packageListData = computed(() => {
  return packageList.value;
});

const activePackageRowKey = computed(() => {
  if (activePackageIndex.value < 0 || activePackageIndex.value >= packageList.value.length) return null;
  const item = packageList.value[activePackageIndex.value];
  return item?.Id ?? item?.id ?? null;
});

// 右侧检测项目列表（构成检测项目包的项目）
const projectListData = ref<any[]>([]);
const projectTableModel = ref<Record<string, string> | undefined>(undefined);
const projectListLoading = ref<boolean>(false);
const projectFilterParams = ref<any[]>([]);
const selectedProjectIds = ref<any[]>([]);
// 保存包内项目的完整数据（通过ID查询获取，用于查看模式显示）
const packageProjectsFullData = ref<any[]>([]);

// 右侧检测项目列表分页
const projectPagination = ref({
  show: true,
  page: 1,
  pageSize: 10,
  total: 0,
  pageSizes: [10, 20, 50, 100],
  layout: 'total, sizes, prev, pager, next, jumper',
  small: true
});

// 根据选中 ID 计算默认选中行数据，供 DynamicTable 使用
const selectedProjectsData = computed(() => {
  if (!Array.isArray(selectedProjectIds.value) || selectedProjectIds.value.length === 0) {
    return [];
  }
  
  if (projectListData.value.length === 0) {
    return [];
  }
  
  const result = projectListData.value.filter(item => {
    const itemId = item.Id || item.id;
    if (!itemId) {
      return false;
    }
    
    // 确保类型一致性，都转换为字符串进行比较
    const itemIdStr = String(itemId);
    const isSelected = selectedProjectIds.value.some(selectedId => String(selectedId) === itemIdStr);
    
    return isSelected;
  });
  
  return result;
});

// 根据编辑模式计算要显示的检测项目数据
const displayProjectListData = computed(() => {
  if (isEditMode.value) {
    // 编辑模式：显示完整的检测项目列表（分页数据）
    return projectListData.value;
  } else {
    // 查看模式：直接返回包内项目数据，el-table 会处理筛选和排序
    return packageProjectsFullData.value;
  }
});

// 加载检测项目包列表
const loadPackageList = async () => {
  try {
    listLoading.value = true;
    // 构建分页参数
    const paginationData = {
      page: packagePagination.value.page,
      intPageSize: packagePagination.value.pageSize
    };
    const res = await ParameterSettings.detectionPackageListIndex(paginationData, filterParams.value);

    if (res && res.status === 200 && res.success && res.response && res.response.data) {
      packageList.value = Array.isArray(res.response.data) ? res.response.data : [];
      // 更新总数 - 使用dataCount字段
      packagePagination.value.total = res.response.dataCount || res.response.total || res.response.count || 0;
      if (res.response.model && typeof res.response.model === 'object') {
        packageTableModel.value = res.response.model;
      } else {
        packageTableModel.value = undefined;
      }
    } else {
      packageList.value = [];
      packageTableModel.value = undefined;
      packagePagination.value.total = 0;
    }
  } catch (error) {
    console.error('加载检测项目包列表失败:', error);
    packageList.value = [];
  } finally {
    listLoading.value = false;
  }
};

// 左侧列表筛选
const handleFilter = (filters: any[]) => {
  filterParams.value = filters;
  packagePagination.value.page = 1; // 筛选时重置到第一页
  loadPackageList();
};

// 分页器事件处理
const handlePackagePageChange = (page: number) => {
  packagePagination.value.page = page;
  loadPackageList();
};

const handlePackagePageSizeChange = (pageSize: number) => {
  packagePagination.value.pageSize = pageSize;
  packagePagination.value.page = 1; // 改变页大小时重置到第一页
  loadPackageList();
};

// 辅助：根据行找到在列表中的索引
const findPackageIndex = (row: any): number => {
  return packageList.value.findIndex((item: any) => {
    if (row.Id !== undefined && item.Id !== undefined) return item.Id === row.Id;
    if (row.id !== undefined && item.id !== undefined) return item.id === row.id;
    return item === row;
  });
};

// 辅助：根据 ID 或名称查找列表项
const findPackageItem = (targetId?: number | string, targetName?: string): any => {
  if (targetId) {
    const targetIdText = String(targetId);
    const foundById = packageList.value.find((item: any) => String(item.Id ?? item.id ?? '') === targetIdText);
    if (foundById) return foundById;
  }
  if (targetName) {
    return packageList.value.find((item: any) => item.名称 === targetName || item.name === targetName);
  }
  return null;
};

// 选择左侧某个检测项目包，加载详情
const handlePackageSelect = async (row: any) => {
  const index = findPackageIndex(row);
  if (index < 0) return;

  activePackageIndex.value = index;
  const item = packageList.value[index];
  const itemId = item.Id || item.id;
  if (!itemId) return;

  try {
    detailLoading.value = true;
    const res = await ParameterSettings.detectionPackageById({ id: itemId });

    if (res && res.status === 200 && res.success && res.response) {
      const data = res.response;

      const projectIds: any[] = [];
      // 兼容两种字段名：检测项目集合 和 检测项目列表
      const projectList = data.检测项目集合 || data.检测项目列表;
      
      // 保存包内项目的完整数据（用于查看模式显示）
      if (Array.isArray(projectList)) {
        packageProjectsFullData.value = projectList;
        projectList.forEach((project: any) => {
          const pid = project.Id || project.id;
          if (pid) {
            projectIds.push(pid);
          }
        });
      } else {
        packageProjectsFullData.value = [];
      }

      currentPackage.value = {
        id: data.id || data.Id,
        name: data.name || data.名称 || ''
      };
      selectedProjectIds.value = projectIds;

      // 默认进入查看模式
      isEditMode.value = false;

      // 使用 nextTick 确保 DOM 更新后再处理选中状态
      await nextTick();

      if (packageFormRef.value) {
        packageFormRef.value.clearValidate?.();
      }
    } else {
      currentPackage.value = null;
      selectedProjectIds.value = [];
      isEditMode.value = false; // 确保进入查看模式
      ElMessage.error('数据加载失败');
    }
  } catch (error) {
    console.error('加载检测项目包详情失败:', error);
    currentPackage.value = null;
    selectedProjectIds.value = [];
    isEditMode.value = false; // 确保进入查看模式
    ElMessage.error('数据加载失败');
  } finally {
    detailLoading.value = false;
  }
};

// DynamicTable 编辑按钮 -> 查看/编辑
const handlePackageEdit = (row: any) => {
  handlePackageSelect(row);
};

// 删除检测项目包
const deletePackage = async (index: number) => {
  const item = packageList.value[index];
  if (!item) return;

  const itemId = item.Id || item.id;

  // 无 ID 的情况（理论上很少出现），直接删除本地项
  if (!itemId) {
    ElMessageBox.confirm('确定要删除这个检测项目包吗？', '提示', {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'warning'
    })
      .then(() => {
        packageList.value.splice(index, 1);
        if (activePackageIndex.value === index) {
          activePackageIndex.value = -1;
          currentPackage.value = null;
          selectedProjectIds.value = [];
        }
        ElMessage.success('删除成功');
      })
      .catch(() => { /* cancelled */ });
    return;
  }

  ElMessageBox.confirm('确定要删除这个检测项目包吗？删除后无法恢复', '提示', {
    confirmButtonText: '确定',
    cancelButtonText: '取消',
    type: 'warning'
  })
    .then(async () => {
      try {
        const res = await ParameterSettings.detectionPackageDelete({ id: itemId });

        if (res && res.status === 200 && res.success) {
          const isDeletingActive = activePackageIndex.value === index;
          const activeItem = activePackageIndex.value >= 0 && activePackageIndex.value < packageList.value.length
            ? packageList.value[activePackageIndex.value]
            : null;
          const activeItemId = activeItem?.Id ?? activeItem?.id;

          if (isDeletingActive) {
            activePackageIndex.value = -1;
            currentPackage.value = null;
            selectedProjectIds.value = [];
            packageProjectsFullData.value = [];
            isEditMode.value = false;
          } else if (activePackageIndex.value > index) {
            activePackageIndex.value--;
          }

          if (packageList.value.length === 1 && packagePagination.value.page > 1) {
            packagePagination.value.page--;
          }
          await loadPackageList();
          if (!isDeletingActive && activeItemId) {
            const foundItem = findPackageItem(activeItemId);
            if (foundItem) {
              activePackageIndex.value = findPackageIndex(foundItem);
            } else {
              activePackageIndex.value = -1;
              currentPackage.value = null;
              selectedProjectIds.value = [];
              packageProjectsFullData.value = [];
              isEditMode.value = false;
            }
          }
          ElMessage.success('删除成功');
        } else {
          ElMessage.error('删除失败');
        }
      } catch (error) {
        console.error('删除检测项目包失败:', error);
        ElMessage.error('删除失败');
      }
    })
    .catch(() => { /* cancelled */ });
};

const handlePackageDelete = (row: any) => {
  const index = findPackageIndex(row);
  if (index >= 0) {
    deletePackage(index);
  }
};

// 新增检测项目包：只在右侧创建一个空包，保存后再刷新左侧列表
const addNewPackage = async () => {
  currentPackage.value = {
    name: ''
  };
  selectedProjectIds.value = [];
  packageProjectsFullData.value = []; // 清空包内项目完整数据
  activePackageIndex.value = -1;
  // 新增时进入编辑模式
  isEditMode.value = true;
  if (packageFormRef.value) {
    packageFormRef.value.resetFields?.();
  }
  
  // 新增时加载检测项目列表数据
  if (projectListData.value.length === 0) {
    await loadProjectList();
  }
};

// 重置当前包：新增时清空，已有时重新加载详情
const resetCurrentPackage = async () => {
  if (!currentPackage.value) return;

  if (currentPackage.value.id) {
    const currentItem =
      activePackageIndex.value >= 0 && activePackageIndex.value < packageList.value.length
        ? packageList.value[activePackageIndex.value]
        : null;
    if (currentItem) {
      await handlePackageSelect(currentItem);
    }
  } else {
    currentPackage.value.name = '';
    selectedProjectIds.value = [];
    packageProjectsFullData.value = []; // 清空包内项目完整数据
    if (packageFormRef.value) {
      packageFormRef.value.resetFields?.();
    }
  }
};

// 加载检测项目列表（右侧 DynamicTable）
const loadProjectList = async () => {
  try {
    projectListLoading.value = true;
    // 构建分页参数
    const paginationData = {
      page: projectPagination.value.page,
      intPageSize: projectPagination.value.pageSize
    };
    const res = await ParameterSettings.parameterSettingsListIndex(paginationData, projectFilterParams.value);

    if (res && res.status === 200 && res.success && res.response && res.response.data) {
      projectListData.value = Array.isArray(res.response.data) ? res.response.data : [];
      // 更新总数 - 使用dataCount字段
      const total = res.response.dataCount || res.response.total || res.response.count || 0;
      projectPagination.value.total = total;
      if (res.response.model && typeof res.response.model === 'object') {
        projectTableModel.value = res.response.model;
      } else {
        projectTableModel.value = undefined;
      }
    } else {
      projectListData.value = [];
      projectTableModel.value = undefined;
      projectPagination.value.total = 0;
    }
  } catch (error) {
    console.error('加载检测项目列表失败:', error);
    projectListData.value = [];
    ElMessage.error('加载检测项目列表失败');
  } finally {
    projectListLoading.value = false;
  }
};

// 处理检测项目筛选（仅编辑模式）
const handleProjectFilter = async (filters: any[], savedSelection?: any[]) => {
  projectFilterParams.value = filters;
  projectPagination.value.page = 1;
  await loadProjectList();
};

// 处理检测项目排序（仅编辑模式）
const handleProjectSort = (column: string, type: number) => {
  // 编辑模式的排序逻辑（如果后端支持）
  console.log('编辑模式排序:', column, type);
};


// 检测项目列表分页器事件处理
const handleProjectPageChange = (page: number) => {
  // 只在编辑模式下处理分页，查看模式下不需要分页
  if (!isEditMode.value) {
    return;
  }

  projectPagination.value.page = page;
  loadProjectList();
  
  // selectedProjectIds 保持不变，DynamicTable 会通过 defaultSelection 自动恢复勾选
};

const handleProjectPageSizeChange = (pageSize: number) => {
  // 只在编辑模式下处理分页，查看模式下不需要分页
  if (!isEditMode.value) {
    return;
  }

  projectPagination.value.pageSize = pageSize;
  projectPagination.value.page = 1; // 改变页大小时重置到第一页
  loadProjectList();
  
  // selectedProjectIds 保持不变，DynamicTable 会通过 defaultSelection 自动恢复勾选
};

// 处理检测项目勾选变化
const handleProjectSelectionChange = (selection: any[]) => {
  // 只在编辑模式下才处理勾选变化，避免查看模式下被意外清空
  if (!isEditMode.value) {
    return;
  }
  
  // 获取当前表格中显示的所有项目ID
  const currentDisplayIds = projectListData.value.map(item => String(item.Id || item.id));
  
  // 获取当前选中的项目ID
  const currentSelectedIds = selection.map(item => String(item.Id || item.id));
  
  // 如果 selection 是空数组，但 selectedProjectIds 有值，
  // 且当前页包含应该被选中的项目，说明是 DynamicTable 初始化导致的误触发，跳过处理
  if (currentSelectedIds.length === 0 && selectedProjectIds.value.length > 0) {
    const shouldBeSelectedInCurrentPage = selectedProjectIds.value.filter(id =>
      currentDisplayIds.includes(String(id))
    );
    if (shouldBeSelectedInCurrentPage.length > 0) {
      // 这是 DynamicTable 初始化时的误触发，不应该清空 selectedProjectIds
      return;
    }
  }
  
  // 保留不在当前显示列表中的已选项目ID（被筛选掉的项目或其他页的项目）
  const hiddenSelectedIds = selectedProjectIds.value.filter(id => 
    !currentDisplayIds.includes(String(id))
  );
  
  // 合并当前选中的ID和被筛选掉的ID
  const allSelectedIds = [...currentSelectedIds, ...hiddenSelectedIds];
  
  selectedProjectIds.value = allSelectedIds;
};

// 保存检测项目包
const savePackageData = async () => {
  if (!currentPackage.value) {
    ElMessage.warning('请先创建或选择检测项目包');
    return;
  }

  if (packageFormRef.value) {
    await packageFormRef.value.validate(async (valid: boolean) => {
      if (!valid) {
        ElMessage.warning('请填写必填项');
        return;
      }

      if (!selectedProjectIds.value || selectedProjectIds.value.length === 0) {
        ElMessage.warning('请至少选择一个检测项目');
        return;
      }

      const pkg = currentPackage.value;
      if (!pkg) {
        ElMessage.warning('请先创建或选择检测项目包');
        return;
      }

      const currentId = pkg.id;
      const isNew = !currentId;

      const submitData: any = {
        名称: pkg.name,
        检测项目集合: selectedProjectIds.value.map((id: any) => ({ Id: id })),
        ISDELETED: false,
        Id: currentId || ''
      };

      try {
        saveLoading.value = true;
        const res = await ParameterSettings.detectionPackageSave(submitData);

        if (res && res.status === 200 && res.success) {
          if (isNew) {
            ElMessage.success('新增成功');
            // 获取返回的ID（后端返回格式：response.ID）
            const newId = res.response?.ID || res.response?.Id || res.response?.id;
            const targetName = pkg.name;
            
            packagePagination.value.page = 1;
            await loadPackageList();
            // 刷新列表后，重新选中当前项（优先通过ID匹配，其次通过名称匹配）
            const foundItem = newId ? findPackageItem(newId) : findPackageItem(undefined, targetName);
            if (foundItem) {
              await handlePackageSelect(foundItem);
              // 保存成功后进入查看模式
              isEditMode.value = false;
            } else if (newId) {
              const detailRes = await ParameterSettings.detectionPackageById({ id: newId });
              if (detailRes && detailRes.status === 200 && detailRes.success && detailRes.response) {
                const data = detailRes.response;
                const projectIds: any[] = [];
                const projectList = data.检测项目集合 || data.检测项目列表;
                packageProjectsFullData.value = Array.isArray(projectList) ? projectList : [];
                packageProjectsFullData.value.forEach((project: any) => {
                  const pid = project.Id || project.id;
                  if (pid) projectIds.push(pid);
                });
                currentPackage.value = {
                  id: data.id || data.Id,
                  name: data.name || data.名称 || ''
                };
                selectedProjectIds.value = projectIds;
                activePackageIndex.value = -1;
                isEditMode.value = false;
              }
            }
          } else {
            ElMessage.success('更新成功');
            const currentItem =
              activePackageIndex.value >= 0 && activePackageIndex.value < packageList.value.length
                ? packageList.value[activePackageIndex.value]
                : null;
            if (currentItem) {
              currentItem.名称 = pkg.name;
              currentItem.name = pkg.name;
            }
            
            // 更新成功后，重新获取包的详细信息以确保数据是最新的
            if (pkg.id) {
              try {
                const detailRes = await ParameterSettings.detectionPackageById({ id: pkg.id });
                if (detailRes && detailRes.status === 200 && detailRes.success && detailRes.response) {
                  const data = detailRes.response;
                  const projectIds: any[] = [];
                  const projectList = data.检测项目集合 || data.检测项目列表;
                  
                  // 更新包内项目的完整数据
                  if (Array.isArray(projectList)) {
                    packageProjectsFullData.value = projectList;
                    projectList.forEach((project: any) => {
                      const pid = project.Id || project.id;
                      if (pid) {
                        projectIds.push(pid);
                      }
                    });
                  } else {
                    packageProjectsFullData.value = [];
                  }
                  
                  selectedProjectIds.value = projectIds;
                }
              } catch (error) {
                console.error('更新后重新加载详情失败:', error);
              }
            }
            
            // 保存成功后进入查看模式
            isEditMode.value = false;
          }
        } else {
          ElMessage.error(isNew ? '新增失败' : '更新失败');
        }
      } catch (error) {
        console.error('保存检测项目包失败:', error);
        ElMessage.error(isNew ? '新增失败' : '更新失败');
      } finally {
        saveLoading.value = false;
      }
    });
  }
};

// 进入编辑模式
const enterEditMode = async () => {
  // 在切换编辑模式之前，保存当前的选择状态
  const currentSelectedProjects = selectedProjectsData.value;
  
  isEditMode.value = true;
  
  // 进入编辑模式时，加载完整的检测项目列表
  await loadProjectList();
  
  // 进入编辑模式后，同步当前选择状态到DynamicTable
  await nextTick();
  
  if (projectTableRef.value && currentSelectedProjects.length > 0) {
    // 初始化DynamicTable的currentSelection状态
    await projectTableRef.value.restoreSelection(currentSelectedProjects);
  }
};

// 退出编辑模式
const exitEditMode = async () => {
  isEditMode.value = false;
  
  // 重新加载当前包的详情，恢复到查看状态
  if (currentPackage.value && currentPackage.value.id) {
    const currentItem = packageList.value.find(item => 
      (item.Id || item.id) === (currentPackage.value?.id)
    );
    if (currentItem) {
      await handlePackageSelect(currentItem);
    }
  }
};


onMounted(() => {
  loadPackageList();
  // 不需要在初始化时加载项目列表，只在编辑模式或需要model时加载
});
</script>

<style lang="less" scoped>
.package-editor-container {
  /* 页面 padding / 背景 / 滚动由 layout 统一处理 */
  height: 100%;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;

  .el-card {
    flex: 1;
    height: 0;
    max-height: 100%;
    display: flex;
    flex-direction: column;

    :deep(.el-card__body) {
      flex: 1;
      height: 0;
      padding: 0;
      overflow: hidden;
      display: flex;
      flex-direction: column;
      min-height: 0;
    }
  }

  .main-content {
    display: flex;
    flex: 1;
    height: 0;
    min-height: 0;
    overflow: hidden;

    .resize-handle {
      width: 6px;
      background: #e4e7ed;
      cursor: col-resize;
      flex-shrink: 0;
      transition: background-color 0.2s;
      position: relative;
      &:hover { background: #409eff; }
      &::before {
        content: '';
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: 2px;
        height: 30px;
        background: #909399;
        border-radius: 1px;
      }
      &:hover::before { background: white; }
    }

    .left-panel {
      flex-shrink: 0;
      border-right: none;
      display: flex;
      flex-direction: column;
      height: 100%;
      min-height: 0;

      .list-header {
        padding: 1rem;
        border-bottom: 1px solid #e4e7ed;
        display: flex;
        align-items: center;
      }

      .package-list-wrapper {
        flex: 1;
        overflow: auto; /* 低分辨率下内容超出时滚动，避免分页器被裁 */
        padding: 0.5rem;
        display: flex;
        flex-direction: column;

        :deep(.dynamic-table-card) {
          box-shadow: none;
          border: none;
          height: 100%;
          min-height: 0 !important; /* 覆盖 DynamicTable 默认 min-height:400px */
          display: flex;
          flex-direction: column;

          .el-card__body {
            flex: 1;
            height: 0;
            overflow: hidden;
            display: flex;
            flex-direction: column;
          }
        }

        :deep(.el-table) {
          height: 100%;

          .el-table__body-wrapper {
            flex: 1;
            height: 0;
          }

          .el-table__row {
            cursor: pointer;

            &:hover {
              background-color: #f5f7fa;
            }

            &.current-row {
              background-color: #ecf5ff;

              td {
                background-color: #ecf5ff;
              }
            }
          }
        }

        .empty-list {
          display: flex;
          align-items: center;
          justify-content: center;
          height: 200px;
          color: #909399;
          font-size: 14px;
        }

      }
    }

    .right-panel {
      flex: 1;
      padding: 1rem;
      overflow: hidden; /* 防止整体滚动 */
      min-width: 0;
      display: flex;
      flex-direction: column;

      .edit-area {
        display: flex;
        flex-direction: column;
        height: 100%;
        min-width: 0;
        overflow: hidden;

        .edit-mode-control {
          margin-bottom: 16px;
          padding-bottom: 16px;
          border-bottom: 1px solid #e4e7ed;
          flex-shrink: 0; /* 防止被压缩 */
        }
      }

      .form-content {
        width: 100%;
        display: flex;
        flex-direction: column;
        height: 100%;
        min-width: 0;
        overflow: hidden;
      }

      .package-name-row {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 16px;
        flex-shrink: 0; /* 防止被压缩 */
        min-width: 0;
        margin-bottom: 16px;

        .package-name-item {
          flex: 1;
          min-width: 0;
          max-width: 100%;
          margin-bottom: 0;
        }

        .action-buttons {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          flex-shrink: 0;
        }
      }

      .project-table-title {
        margin: 10px 0;
        font-weight: 500;
        flex-shrink: 0; /* 防止被压缩 */
      }

      /* 检测项目表格容器：占满右侧剩余高度，低分辨率下允许压缩并滚动 */
      .project-table-container {
        flex: 1;
        height: auto;
        min-height: 0;
        min-width: 0;
        display: flex;
        flex-direction: column;
        overflow: auto; /* 双向都允许滚动，防止表格内容超出被裁掉 */
        
        :deep(.dynamic-table-card) {
          height: 100%;
          min-height: 0 !important; /* 覆盖 DynamicTable 默认的 min-height:400px，防止把分页器挤出容器 */
          display: flex;
          flex-direction: column;
          overflow: visible; /* 让表格横向滚动条不被卡片裁掉 */
          
          .el-card__body {
            flex: 1;
            display: flex;
            flex-direction: column;
            overflow: visible; /* 让 el-table 的横向滚动条可见 */
          }
          
          .el-table {
            flex: 1;
            min-height: 0;
            overflow: auto;
          }
          
          .pagination-wrapper {
            flex-shrink: 0; /* 防止分页器被压缩 */
          }
        }
      }

      /* 底部按钮区域 */
      .el-form-item:last-child {
        flex-shrink: 0; /* 防止被压缩 */
        margin-top: 16px;
        margin-bottom: 0;
      }

      .empty-state {
        display: flex;
        align-items: center;
        justify-content: center;
        height: 100%;
      }
    }
  }
}

/* 平板/窄屏下左右分栏改为上下堆叠，避免右侧表格被遮盖 */
@media (max-width: 768px) {
  .package-editor-container {
    .main-content {
      flex-direction: column;
      height: auto;
      min-height: 0;
      overflow: hidden;

      .resize-handle {
        display: none;
      }

      .left-panel {
        width: 100% !important;
        height: 40%;
        min-height: 180px;
        border-right: none;
        border-bottom: 1px solid #e4e7ed;
      }

      .right-panel {
        height: 60%;
        min-height: 280px;
        padding: 0.75rem;
      }
    }
  }
}
</style>

